import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fileUrl, youtubeId } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PostCard } from "@/pages/Home";
import useSeo from "@/lib/useSeo";

export function Journal() {
  const { t } = useApp();
  const [posts, setPosts] = useState([]);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const k = q.trim().toLowerCase();
  const list = posts.filter((p) => !k || [p.title, p.excerpt].some((x) => (x || "").toLowerCase().includes(k)));
  useSeo({ title: t("journal"), description: "Stories, leather care guides and video tutorials from Craftendemic." });
  useEffect(() => { api.get("/posts", { params: { kind } }).then((r) => setPosts(r.data)); }, [kind]);
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-14" data-testid="journal-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
        <h1 className="font-serif text-5xl">{t("journal").toUpperCase()}</h1>
        <div className="flex flex-wrap gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_news")} className="border border-[#E6DFD5] bg-white px-3 py-2 text-sm outline-none w-56" data-testid="journal-search-input" />
          {[["", t("all")], ["blog", "Blog"], ["vlog", "Vlog"]].map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`journal-filter-${k || "all"}`} className={`px-4 py-2 text-sm border ${kind === k ? "bg-[#3B220C] text-white border-[#3B220C]" : "border-[#E6DFD5]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-x-6 gap-y-14">{list.map((p, i) => <PostCard key={p.post_id} p={p} i={i} />)}</div>
      {!list.length && <p className="text-[#6E6259] py-16 text-center" data-testid="journal-empty">{t("search_empty")}</p>}
    </div>
  );
}

export function Post() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/posts/${slug}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [slug]);
  useSeo({
    title: p ? p.seo_title || p.title : "", description: p ? p.seo_description || p.excerpt : "", image: p ? fileUrl(p.cover) : "",
    jsonLd: p ? { "@type": p.kind === "vlog" ? "VideoObject" : "BlogPosting", headline: p.title, name: p.title, description: p.excerpt,
      image: p.cover, thumbnailUrl: p.cover, datePublished: p.published_at, uploadDate: p.published_at, author: { "@type": "Organization", name: "Craftendemic" } } : null,
  });
  if (p === false) return <p className="text-center py-32">Post not found.</p>;
  if (!p) return <div className="h-[60vh]" />;
  const yt = youtubeId(p.video_url);
  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-8 py-14 space-y-8" data-testid="post-page">
      <p className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{p.kind.toUpperCase()} · {new Date(p.published_at).toLocaleDateString()}</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="post-title">{p.title}</h1>
      {yt ? <div className="aspect-video"><iframe className="w-full h-full" title={p.title} src={`https://www.youtube.com/embed/${yt}`} allowFullScreen data-testid="post-video" /></div>
        : p.video_url ? <video src={fileUrl(p.video_url)} controls className="w-full" data-testid="post-video" />
        : p.cover ? <img src={fileUrl(p.cover)} alt={p.title} className="w-full aspect-[16/9] object-cover" /> : null}
      <div className="prose-craft text-lg">{(p.content || "").split(/\n\n+/).map((para, i) => <p key={i}>{para}</p>)}</div>
    </article>
  );
}

export default Journal;
